import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

import type { CardContentKind } from "#card-content";
import { cardContentKindFromWordCardKind } from "#word-card-kind-mapping";

/**
 * The actor-side overlay presence flags this resolver reads. Callers that join only some
 * overlays pass the ones they have; an omitted overlay is treated as absent.
 *
 * Deliberately loose (`unknown | null | undefined` rather than the row types) because callers
 * project different column subsets — the due-count badge joins
 * `userGraphemePronunciationCard: true` with no `columns`, while the deck reads select
 * specific columns. Only presence matters here.
 */
export type UserCardOverlayPresence = {
  userGraphemeMeaningCard?: unknown;
  userGraphemePronunciationCard?: unknown;
  /**
   * The word overlay is the one exception to "only presence matters": all three word kinds live in
   * ONE table, so the row's `kind` column is the only thing that distinguishes them. A caller that
   * joins this overlay without selecting `kind` would have every word card resolve to a single
   * kind and be gated by the wrong review-eligibility setting — so this field is typed to require
   * it rather than left `unknown`.
   */
  userQuizCard?: unknown;
  userWordSentenceCard?: { kind: WordCardKind } | null;
};

/**
 * Resolve a `user_card`'s content kind from which actor overlay rows it carries.
 *
 * The actor-side counterpart to `resolveAppCardContent`, for reads that never touch the
 * content DB. The due-count badge previously spelled this rule out inline as
 *
 * ```ts
 * return isPinyinFlashcardsEnabled || row.userGraphemePronunciationCard === null;
 * ```
 *
 * i.e. it inferred "this is not a pronunciation card" from the *absence* of a pronunciation
 * overlay. That is right for exactly two kinds and wrong for any third: a card of a new kind
 * also has no pronunciation overlay, so it would be silently treated as a meaning card and
 * counted as always-due — a badge that disagrees with the scheduler, with nothing to indicate
 * why.
 *
 * Returns `null` when no known overlay is present. Callers must decide what that means for
 * them; it covers both a not-yet-synced overlay and a card of a kind this build doesn't know
 * about, and conflating either with "meaning" is the bug above.
 */
export function resolveUserCardContentKind(
  overlays: UserCardOverlayPresence,
): CardContentKind | null {
  if (
    overlays.userGraphemePronunciationCard !== null &&
    overlays.userGraphemePronunciationCard !== undefined
  ) {
    return "grapheme-pronunciation";
  }
  if (overlays.userGraphemeMeaningCard !== null && overlays.userGraphemeMeaningCard !== undefined) {
    return "grapheme-meaning";
  }
  if (overlays.userWordSentenceCard !== null && overlays.userWordSentenceCard !== undefined) {
    return cardContentKindFromWordCardKind(overlays.userWordSentenceCard.kind);
  }
  if (overlays.userQuizCard !== null && overlays.userQuizCard !== undefined) {
    return "quiz";
  }
  return null;
}
